import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from "@angular/common/http";
import { map, Observable, tap } from "rxjs";
import { AuthService } from "./auth.service";

export abstract class AbstractHttpService {
	constructor(private httpClient: HttpClient, private context: string, private authService: AuthService | undefined) { }

	protected doGet<T>(extendAuth: boolean, path: string): Observable<T> {
		return this.httpClient.get<T>(`${this.context}${path}`, {
			headers: this.getHeaders(extendAuth),
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doPost<T>(extendAuth: boolean, path: string, payload: any): Observable<T> {
		return this.httpClient.post<T>(`${this.context}${path}`, payload, {
			headers: this.getHeaders(extendAuth),
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doPostWithAuth<T>(path: string, jwtToken: string, payload: any): Observable<T> {
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${jwtToken}`);

		return this.httpClient.post<T>(`${this.context}${path}`, payload, {
			headers: headers,
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doUploadFile<T>(extendAuth: boolean, path: string, payload: any): Observable<HttpEvent<T>> {
		const formData = new FormData();
		formData.append('file', payload);

		return this.httpClient.post<T>(`${this.context}${path}`, formData, {
			headers: this.getHeaders(extendAuth),
			reportProgress: true,
			observe: 'events'
		});
	}
	protected doUploadFileWithAuth<T>(path: string, jwtToken: string, payload: any): Observable<HttpEvent<T>> {
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${jwtToken}`);

		const formData = new FormData();
		formData.append('file', payload);

		return this.httpClient.post<T>(`${this.context}${path}`, formData, {
			headers: headers,
			reportProgress: true,
			observe: 'events'
		});
	}
	protected doDownloadFile(extendAuth: boolean, path: string): Observable<HttpResponse<Blob>> {
		return this.httpClient.get(`${this.context}${path}`, {
			headers: this.getHeaders(extendAuth),
			responseType: 'blob',
			observe: 'response'
		}).pipe(tap(response => {
			const contentDisposition = response.headers.get('Content-Disposition');
			const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
			const filename = filenameMatch ? filenameMatch[1] : 'download.csv';

			const blob = new Blob([response.body!], { type: response.headers.get('Content-Type') || 'application/octet-stream' });

			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = filename;
			link.click();

			window.URL.revokeObjectURL(link.href);
		}));
	}

	private getHeaders(extendAuth: boolean) {
		if (this.authService == undefined) {
			return new HttpHeaders();
		}
		const token = this.authService.getJwtToken();
		if (token == null) {
			return new HttpHeaders();
		}
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${token}`);
		if (extendAuth == true) {
			headers = headers.set('Update-JWT', 'true');
		}
		return headers;
	}
	private updateAuth(response: HttpHeaders) {
		if (this.authService == undefined) {
			return;
		}
		const expiresIn = response.get('Expires-In');
		const jwtToken = response.get('Jwt-Token');

		if (expiresIn && jwtToken) {
			this.authService.updateAuthenticate(true, jwtToken, +expiresIn);
		}
	}
}
